"use client";

import { Dialog, DialogContent } from "@/components/ui/dialog";
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverContentProps,
} from "@/components/ui/popover";
import React, { JSX, ReactNode, useEffect, useState } from "react";
import { ActionFeedbackContext, useActionFeedback } from "./context";
import { cn } from "@/lib/utils/utils";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

export function Feedback({
  children,
  asSlot = false,
}: {
  children?: ReactNode;
  asSlot?: boolean;
}) {
  const [feedback, setFeedback] = useState<JSX.Element | undefined>(undefined);
  return (
    <ActionFeedbackContext.Provider value={[feedback, setFeedback]}>
      {feedback && !asSlot
        ? React.cloneElement(feedback, { client: children })
        : children}
    </ActionFeedbackContext.Provider>
  );
}

export function FeedbackSlot({ children }: { children?: ReactNode }) {
  const [feedback] = useActionFeedback();
  return feedback
    ? React.cloneElement(feedback, { client: children })
    : children;
}

export function FeedbackDialog({
  children,
  client,
}: {
  children?: ReactNode;
  client?: ReactNode;
}) {
  const [isOpen, setIsOpen] = useState(true);
  const [feedback] = useActionFeedback();
  useEffect(() => {
    if (feedback) {
      setIsOpen(true);
    }
  }, [feedback]);
  return (
    <>
      {client}
      <Dialog open={isOpen} onOpenChange={() => setIsOpen(false)}>
        <DialogContent>{children}</DialogContent>
      </Dialog>
    </>
  );
}

export function FeedbackToast({
  children,
  client,
  variant = "default",
  data,
}: {
  children?: ReactNode | string;
  client?: ReactNode;
  variant: "success" | "loading" | "error" | "default";
  data?: {
    description?: React.ReactNode;
    duration?: number;
    richColors?: boolean;
    invert?: boolean;
    closeButton?: boolean;
    dismissible?: boolean;
  };
}) {
  const [feedback] = useActionFeedback();
  useEffect(() => {
    if (children) {
      if (variant === "default") {
        toast(children, data);
      } else {
        toast[variant](children, data);
      }
    }
  }, [feedback, data, children, variant]);
  return <>{client}</>;
}

export function FeedbackPopover({
  client,
  children,
  className,
  ...props
}: {
  children?: ReactNode;
  client?: ReactNode;
} & PopoverContentProps) {
  const [feedback] = useActionFeedback();
  const [isOpen, setIsOpen] = useState(true);
  useEffect(() => {
    if (feedback) {
      setIsOpen(true);
    }
  }, [feedback]);
  return (
    <Popover open={!!isOpen} onOpenChange={setIsOpen}>
      <PopoverAnchor asChild>{client}</PopoverAnchor>
      <PopoverContent
        sideOffset={12}
        align="start"
        className={cn("max-w-[800px]", className)}
        {...props}
      >
        {children}
      </PopoverContent>
    </Popover>
  );
}

export function FeedbackSwap(props: { children?: ReactNode }) {
  return props.children;
}

export function showCenteredToast(
  message: string,
  variant: "success" | "loading" | "error" | "default" = "default",
  options?: { size?: "small" | "medium" | "large" }
) {
  const toastOptions = {
    position: "bottom-center" as const,
    autoClose: 3000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
    className: options?.size === "small" 
      ? "text-sm" 
      : options?.size === "large" 
      ? "text-lg" 
      : "text-base", // Default to medium size
  };

  if (variant === "default") {
    toast(message, toastOptions);
  } else {
    toast[variant](message, toastOptions);
  }
}

export function ToastProvider({ children }: { children: ReactNode }) {
  return (
    <>
      {children}
      <ToastContainer />
    </>
  );
}
